import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plane, Car, Hotel, MapPin, Clock, ParkingCircle, Bus, Train } from 'lucide-react';
import { defaultTravelContent } from '../../data/pageContentDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon name → component map (kept in sync with AdminTravelManager.TRAVEL_ICONS)
const ICONS = { Plane, Car, ParkingCircle, Hotel, MapPin, Clock, Bus, Train };

const TravelSection = () => {
  const [c, setC] = useState(defaultTravelContent);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/travel/content`, { params: { _: Date.now() } });
        if (!alive || !res.data) return;
        setC({ ...defaultTravelContent, ...res.data });
      } catch { /* keep defaults */ }
    };
    fetchContent();
    const interval = setInterval(fetchContent, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const cards = c.cards || [];
  const hotels = c.hotels || [];

  return (
    <section id="travel" className="section bg-ivory" data-testid="travel-section">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">{c.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight">
            {c.title}
          </h2>
          <div className="gold-divider" />
        </motion.div>

        {/* Travel Info Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {cards.map((card, i) => {
            const Icon = ICONS[card.icon] || Plane;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.1 }}
                className="wedding-card text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C8A951]/10 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-[#C8A951]" />
                </div>
                <h3 className="font-serif text-xl text-[#3B2F2F] mb-2">{card.title}</h3>
                {card.subtitle && (
                  <p className="text-[#5A4A4A] text-sm mb-2">
                    <strong>{card.subtitle}</strong>
                  </p>
                )}
                {card.description && <p className="text-[#5A4A4A] text-sm">{card.description}</p>}
              </motion.div>
            );
          })}
        </div>

        {/* Arrival Time Notice */}
        {c.arrivalNotice && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-[#C8A951]/10 rounded-2xl p-6 mb-12 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-[#C8A951]" />
              <span className="font-serif text-lg text-[#3B2F2F]">Arrival Time</span>
            </div>
            <p className="text-[#5A4A4A]">{c.arrivalNotice}</p>
          </motion.div>
        )}

        {/* Accommodations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Hotel className="w-6 h-6 text-[#C8A951]" />
            <h3 className="font-serif text-2xl text-[#3B2F2F]">Where to Stay</h3>
          </div>
          {c.accommodationsIntro && (
            <p className="text-[#5A4A4A] max-w-2xl mx-auto">{c.accommodationsIntro}</p>
          )}
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {hotels.map((hotel, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
              className="wedding-card"
            >
              {hotel.type && (
                <div className="inline-block px-3 py-1 bg-[#C8A951]/10 rounded-full text-[#C8A951] text-xs uppercase tracking-wider mb-3">
                  {hotel.type}
                </div>
              )}
              <h4 className="font-serif text-lg text-[#3B2F2F] mb-2">{hotel.name}</h4>
              {hotel.description && <p className="text-[#5A4A4A] text-sm mb-2">{hotel.description}</p>}
              {hotel.distance && (
                <div className="flex items-center gap-1 text-[#C8A951] text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{hotel.distance}</span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TravelSection };
